import React, { useEffect, useState, useRef } from 'react';
import 'antd/dist/antd.css';
import routePaths, { isBasePath, isRouteDefined } from 'config/routes';
import { AppContext, AppProps } from 'next/app';
import getConfig from 'next/config';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import 'nprogress/nprogress.css';
import { IntlProvider } from 'react-intl';
import { useSelector } from 'react-redux';
import redirectTo from 'services/redirectTo';
import { fetchPlatformStatus, fetchPublicMenu } from 'src/actions/appActions';
import { fetchUserData, impersonate } from 'src/actions/authActions';
import OutOfService from 'src/components/general/OutOfService';
import BasicLayout from 'src/components/layout/BasicLayout';
import { intlLocaleProvider } from 'src/intl';
import { IRootReducers } from 'src/reducers';
import { wrapper, store } from 'src/store';
import * as api from 'services/api';
import 'src/styles/global.scss';
import apiPaths from 'services/apiPaths';
import {
  cleanUrl,
  dispatchPageEvent,
  dispatchPageViewEvent,
  getSeoTags,
  getTracking,
  getUserAuthStatus,
  validAnalyticsPage,
} from 'utils';
import { Store } from 'redux';

const { DEFAULT_LANGUAGE, PLATFORM_PREFIX } = getConfig().publicRuntimeConfig;
const { STATIC, IMPERSONATE } = routePaths;

const TopProgressBar = dynamic(() => import('src/components/TopProgressBar'), {
  ssr: false,
});

const isDetailPage = (url: string) => {
  return !![
    routePaths.PAGES.EXPERIENCE_DETAIL,
    routePaths.PAGES.FORMATION_DETAIL,
    routePaths.PAGES.CAMPAIGN_DETAIL,
    routePaths.PAGES.CHALLENGE_DETAIL,
  ].find((route) => {
    return route === cleanUrl(url);
  });
};

const fetchUserDataPolling = async () => {
  try {
    const userRole = await api.getUserDermoRole({
      dataPath: `${apiPaths.DERMO.USER_ROLES}${apiPaths.DERMO.GET_DERMO_USER_ROLE}`,
    });
    const userData = userRole.data;

    store.dispatch((dispatch) => {
      dispatch({ type: 'SET_USER_DATA', payload: userData });
    });

    return userData;
  } catch (error) {
    console.error('Error fetching user data:', error);
  }
};

function MyApp(props: AppProps) {
  const { query, asPath, pathname } = props.router;
  const [initTracked, setInitTracked] = useState(false);
  const isBrowser = typeof window !== 'undefined';
  const user = useSelector((state: IRootReducers) => state.auth.user);
  const challenge = useSelector((state: IRootReducers) => state.app.challenge);
  let userPrev = useRef(user);
  let challengePrev = useRef(challenge);
  let asPathPrev = useRef(asPath);
  let pathnamePrev = useRef(asPath);

  const dermoPrev = useRef<number | undefined>(undefined);
  const dermoStatusPrev = useRef<boolean | undefined>(undefined);

  if (isBrowser && !initTracked) {
    dispatchPageEvent(user ?? null);
  }

  const component = <props.Component {...props.pageProps} />;
  const meta = getSeoTags(props.router, props.pageProps.isUserLogged);

  const handleUserOrChallengeChanged = () => {
    dispatchPageViewEvent(props.router, null, user, challenge);
  };

  const handleRouteChanged = () => {
    if (isDetailPage(pathname) || isDetailPage(pathnamePrev.current)) return;
    dispatchPageViewEvent(props.router, null, user, challenge);
  };

  const pollUserData = async () => {
    const userData = await fetchUserDataPolling();
    const userDermo = userData?.userStatus;
    const status = userData?.status;

    const dermoStatusHasChanged =
      dermoPrev.current !== undefined &&
      dermoPrev.current != userDermo &&
      status === true;

    const dermoStatusRejected =
      dermoPrev.current == userDermo &&
      status === false &&
      status !== dermoStatusPrev.current &&
      (dermoPrev.current === 3 || dermoPrev.current === 2);

    if (userDermo) {
      if (dermoStatusHasChanged || dermoStatusRejected) {
        window.location.reload();
      }
    }
    dermoPrev.current = userDermo;
    dermoStatusPrev.current = status;
  };

  useEffect(() => {
    const dataNotChanged =
      userPrev?.current?.idUser === user?.idUser &&
      challengePrev?.current?.challengeId === challenge?.challengeId;
    const routeNotChanged = asPathPrev?.current === asPath;
    const nothingChanged = dataNotChanged && routeNotChanged;

    if ((initTracked && nothingChanged) || !validAnalyticsPage(asPath)) {
      return;
    }

    if (!dataNotChanged) {
      handleUserOrChallengeChanged();
    } else {
      handleRouteChanged();
    }

    asPathPrev.current = asPath;
    pathnamePrev.current = pathname;
    userPrev.current = user;
    challengePrev.current = challenge;
    document?.querySelector('body')?.scrollTo(0, 0);
    if (!initTracked) setInitTracked(true);
  }, [asPath, challenge]);

  useEffect(() => {
    if (query) getTracking(query);
  }, [query]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    if (user?.uuidUser) {
      const pollingInterval = 10000;

      pollUserData(); // Poll user data every 10 seconds

      intervalId = setInterval(pollUserData, pollingInterval);
    }
    return () => clearInterval(intervalId);
  }, [user]);

  useEffect(() => {
    // Set initial user data
    if (user?.uuidUser) {
      dispatchPageEvent(user); // Dispatch page event when user is logged in
      pollUserData();
    }
  }, []);

  const localeMessages = intlLocaleProvider(DEFAULT_LANGUAGE);

  if (props.pageProps.platformStatus === false) {
    return (
      <IntlProvider
        key="app-intl-provider"
        locale={DEFAULT_LANGUAGE}
        messages={localeMessages}
        onError={(error) => {
          console.error(error);
        }}>
        <OutOfService />
      </IntlProvider>
    );
  }

  if (props.pageProps.pathname === routePaths.HEALTH) {
    return component;
  }

  return (
    <>
      <Head>
        <meta
          key="meta-content-type"
          httpEquiv="Content-Type"
          content="text/html"
          charSet="utf-8"
        />
        <meta
          key="meta-viewport"
          name="viewport"
          content="initial-scale=1.0, width=device-width"
        />
        {meta.description && (
          <meta name="description" content={meta.description} />
        )}
        <title key="app-title">{meta.title}</title>
      </Head>
      <TopProgressBar />
      <IntlProvider
        key="app-intl-provider"
        locale={DEFAULT_LANGUAGE}
        messages={localeMessages}
        onError={(error) => {
          console.error(error);
        }}>
        <BasicLayout>{component}</BasicLayout>
      </IntlProvider>
    </>
  );
}

MyApp.getInitialProps = wrapper.getInitialAppProps(
  (store: Store<IRootReducers>) => async (context: AppContext) => {
    const { Component, ctx } = context;
    const pathname = (ctx.req?.url ?? ctx.pathname).replace(
      PLATFORM_PREFIX,
      '',
    );

    await Promise.all([
      await fetchPlatformStatus(ctx),
      await fetchPublicMenu(ctx),
    ]);

    const impersonating = pathname.includes(IMPERSONATE);

    switch (true) {
      case impersonating:
        await impersonate(ctx);
        break;
      case isBasePath(pathname):
        const fullPath = `${routePaths.INDEX}${pathname}`;
        redirectTo(fullPath, ctx);
        return;
      case isRouteDefined(pathname):
        const initialData = await fetchUserData(ctx);
        if (!Boolean(initialData)) {
          return;
        }
        break;
      default:
        redirectTo(`${STATIC}?page=${encodeURIComponent(pathname)}`, ctx);
        return;
    }

    let initialProps = {};

    if (Component.getInitialProps) {
      initialProps = await Component.getInitialProps(ctx);
    }

    return {
      pageProps: {
        ...initialProps,
        pathname: ctx.pathname,
        isUserLogged: getUserAuthStatus(ctx),
        platformStatus: store.getState().app.platformStatus,
      },
    };
  },
);

/*
MyApp.getInitialProps = async ({
  Component,
  ctx,
}: ReduxPageContext & AppContext) => {
  const context = ctx as ReduxPageContext;
  const pathname = (context.req?.url ?? context.pathname).replace(
    PLATFORM_PREFIX,
    '',
  );

  await Promise.all([
    await fetchPlatformStatus(ctx),
    await fetchPublicMenu(ctx),
  ]);
  const impersonating = pathname.includes(IMPERSONATE);

  switch (true) {
    case impersonating:
      await impersonate(ctx);
      break;
    case isBasePath(pathname):
      const fullPath = `${routePaths.INDEX}${pathname}`;
      redirectTo(fullPath, ctx);
      return;
    case isRouteDefined(pathname):
      const initialData = await fetchUserData(ctx);
      if (!Boolean(initialData)) {
        return;
      }
      break;
    default:
      redirectTo(`${STATIC}?page=${encodeURIComponent(pathname)}`, ctx);
      return;
  }

  let initialProps = {};

  if (Component.getInitialProps) {
    initialProps = await Component.getInitialProps(ctx);
  }

  return {
    pageProps: {
      ...initialProps,
      pathname: context.pathname,
      isUserLogged: getUserAuthStatus(context),
      platformStatus: context.store.getState().app.platformStatus,
    },
  };
};
*/
export default wrapper.withRedux(MyApp);

// Only uncomment this method if you have blocking data requirements for
// every single page in your application. This disables the ability to
// perform automatic static optimization, causing every page in your app to
// be server-side rendered.
//
// MyApp.getInitialProps = async (appContext) => {
//   // calls page's `getInitialProps` and fills `appProps.pageProps`
//   const appProps = await App.getInitialProps(appContext);
//
//   return { ...appProps }
// }
